<script setup>
import { computed, ref } from 'vue'
import { Link, usePage, router } from '@inertiajs/vue3'
import {
    PhSignOut, PhUser, PhList, PhX, PhCaretDown,
} from '@phosphor-icons/vue'
import logo      from '@/../images/logo.jpg'
import AppLoader from '@pages/Shared/AppLoader.vue'

const page      = usePage()
const applicant = computed(() => page.props?.auth?.user || {})

const mobileOpen   = ref(false)
const dropdownOpen = ref(false)

const logout = () => router.post('/logout')

const navLinks = [
    { label: 'Dashboard',   href: '/applicant/dashboard'   },
    { label: 'Application', href: '/applicant/application' },
    { label: 'Payments',    href: '/applicant/payments'    },
]

const onClickOutside = () => { dropdownOpen.value = false }
</script>

<template>
    <div class="min-h-screen flex flex-col bg-background">

        <header class="sticky top-0 z-50 bg-white border-b border-gray-200">
            <div class="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">

                <!-- Logo -->
                <Link href="/applicant/dashboard" class="flex items-center gap-3 shrink-0">
                    <img :src="logo" class="w-9 h-9 rounded-lg object-cover shadow" alt="Logo" />
                    <div class="block">
                        <p class="text-sm font-bold text-primary leading-tight">Bn Mas'ud College</p>
                        <p class="text-[10px] text-gray-400">Applicant Portal</p>
                    </div>
                </Link>

                <!-- Desktop nav -->
                <nav class="hidden md:flex items-center gap-1">
                    <Link
                        v-for="link in navLinks"
                        :key="link.href"
                        :href="link.href"
                        class="px-4 py-2 rounded-lg text-sm font-medium transition"
                        :class="$page.url.startsWith(link.href)
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'"
                    >
                        {{ link.label }}
                    </Link>
                </nav>

                <!-- Right: user dropdown -->
                <div class="flex items-center gap-2">

                    <!-- User dropdown trigger -->
                    <div class="relative" v-click-outside="onClickOutside">
                        <button
                            @click="dropdownOpen = !dropdownOpen"
                            class="flex items-center gap-2 px-3 py-1.5 rounded-lg
                                   bg-gray-50 border border-gray-200 hover:bg-gray-100
                                   transition focus:outline-none"
                        >
                            <div class="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <PhUser :size="14" class="text-primary" />
                            </div>
                            <div class="hidden sm:block text-left">
                                <p class="text-xs font-semibold text-gray-800 leading-tight">
                                    {{ applicant.first_name ?? applicant.name ?? '' }}
                                    {{ applicant.last_name ?? '' }}
                                </p>
                                <p class="text-[10px] text-gray-400 leading-tight">{{ applicant.id }}</p>
                            </div>
                            <PhCaretDown
                                :size="13"
                                class="text-gray-400 transition-transform duration-200 hidden sm:block"
                                :class="dropdownOpen ? 'rotate-180' : ''"
                            />
                        </button>

                        <!-- Dropdown -->
                        <transition
                            enter-active-class="transition ease-out duration-150"
                            enter-from-class="opacity-0 translate-y-1"
                            enter-to-class="opacity-100 translate-y-0"
                            leave-active-class="transition ease-in duration-100"
                            leave-from-class="opacity-100 translate-y-0"
                            leave-to-class="opacity-0 translate-y-1"
                        >
                            <div
                                v-if="dropdownOpen"
                                class="absolute right-0 mt-2 w-48 bg-white rounded-xl border border-gray-200
                                       shadow-lg overflow-hidden z-50"
                            >
                                <!-- Info header -->
                                <div class="px-4 py-3 border-b border-gray-100 bg-gray-50">
                                    <p class="text-xs font-semibold text-gray-800 truncate">
                                        {{ applicant.first_name ?? applicant.name ?? '' }}
                                        {{ applicant.last_name ?? '' }}
                                    </p>
                                    <p class="text-[10px] text-gray-400 font-mono mt-0.5">{{ applicant.id }}</p>
                                </div>

                                <div class="py-1">
                                    <hr class="my-1 border-gray-100" />
                                    <button
                                        @click="logout"
                                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm
                                               text-red-500 hover:bg-red-50 transition"
                                    >
                                        <PhSignOut :size="15" />
                                        Sign Out
                                    </button>
                                </div>
                            </div>
                        </transition>
                    </div>

                    <!-- Mobile toggle -->
                    <button
                        @click="mobileOpen = !mobileOpen"
                        class="md:hidden p-2 rounded-lg hover:bg-gray-100 transition"
                    >
                        <PhList v-if="!mobileOpen" :size="20" />
                        <PhX    v-else              :size="20" />
                    </button>
                </div>
            </div>

            <!-- Mobile nav -->
            <transition name="fade-slide">
                <div v-if="mobileOpen" class="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-1">
                    <Link
                        v-for="link in navLinks"
                        :key="link.href"
                        :href="link.href"
                        @click="mobileOpen = false"
                        class="block px-4 py-2.5 rounded-lg text-sm font-medium transition"
                        :class="$page.url.startsWith(link.href)
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-600 hover:bg-gray-100'"
                    >
                        {{ link.label }}
                    </Link>

                    <hr class="border-gray-100 my-1" />

                    <button
                        @click="logout"
                        class="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm
                               font-medium text-red-500 hover:bg-red-50 transition"
                    >
                        <PhSignOut :size="16" />
                        Sign Out
                    </button>
                </div>
            </transition>
        </header>

        <main class="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-8">
            <slot />
        </main>

        <footer class="mt-auto bg-white border-t border-gray-200 py-5 text-center text-xs text-gray-500">
            © {{ new Date().getFullYear() }} Bn Mas'ud College. All rights reserved.
        </footer>

        <AppLoader />
    </div>
</template>

<style scoped>
.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.2s ease; }
.fade-slide-enter-from,  .fade-slide-leave-to    { opacity: 0; transform: translateY(-6px); }
</style>